import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
  Tooltip,
  FormControlLabel,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import OddsHistoryModal, { useOddsHistory } from '../components/OddsHistoryModal';

// ══════════════════════════════════════════════════════════
//  FILTER OPTIONS
//  Markets map directly to OpticOdds market base types.
//  Period types are parsed from market_id prefixes by the server.
// ══════════════════════════════════════════════════════════

const defaultMarkets = ['Total', 'Spread', 'Moneyline', 'Summary'];

const marketFilterMap = {
  Total:     'total_points',
  Spread:    'point_spread',
  Moneyline: 'moneyline',
};

// Market equivalences across sports (e.g. NHL puck_line = NBA point_spread)
const SPREAD_MARKETS = new Set(['point_spread', 'puck_line', 'game_spread', 'run_line', 'kills_handicap', 'asian_handicap']);
const TOTAL_MARKETS  = new Set(['total_points', 'total_goals', 'total_games', 'total_runs', 'total_kills']);

// Sport-aware market filter: maps display label → market_id per sport
const marketFilterBySport = {
  NHL: { Spread: 'puck_line', Total: 'total_goals', Moneyline: 'moneyline' },
  ATP: { Spread: 'game_spread', Total: 'total_games', Moneyline: 'moneyline' },
  WTA: { Spread: 'game_spread', Total: 'total_games', Moneyline: 'moneyline' },
  MLB: { Spread: 'run_line', Total: 'total_runs', Moneyline: 'moneyline' },
  PGA: { Moneyline: 'moneyline' },
  'League of Legends': { Spread: 'kills_handicap', Total: 'total_kills', Moneyline: 'moneyline' },
  CS2: { Spread: 'kills_handicap', Total: 'total_kills', Moneyline: 'moneyline' },
};

const periodTypesBySport = {
  NBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  CBB: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  WNBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  CFB: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  WTA: ['Full Game', 'Set 1', 'Set 2'],
  ATP: ['Full Game', 'Set 1', 'Set 2'],
  'League of Legends': ['Full Game', 'Map 1', 'Map 2', 'Map 3', 'Map 4', 'Map 5'],
  CS2: ['Full Game', 'Map 1', 'Map 2', 'Map 3', 'Map 4', 'Map 5'],
  '__soccer__': ['Full Game', 'First Half'],
};

// Markets that only exist at map/period level (no Full Game equivalent)
const noFullGameMarkets = {
  'League of Legends': new Set(['Total', 'Spread', 'Summary']),
  CS2: new Set(['Total', 'Spread', 'Summary']),
};

const marketsBySport = {
  PGA: ['Moneyline'],
};

const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel':'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// ══════════════════════════════════════════════════════════
//  STATIC STYLES — hoisted outside component to avoid per-render allocation
// ══════════════════════════════════════════════════════════

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const SX_COL_OUTER = { display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' };
const SX_EMPTY_CELL = { textAlign: 'right', pr: '12px' };
const STYLE_BEST_IMG = { width: '30px', height: '30px', objectFit: 'contain' };
const FIXED_COLUMNS = ['eventName', 'eventStartFormatted', 'bestOdds'];

// Format limits for display (e.g. "$1.5k", "$998")
const formatLimitMax = (limits) => {
  const max = limits?.max;
  if (max == null) return null;
  return max >= 1000 ? `$${(max / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(max)}`;
};

// ── Flash-fade: pure CSS animation replaces per-second JS timer ──
const FLASH_DURATION_SEC = 300;
const FLASH_KEYFRAMES_ID = 'flash-fade-keyframes';

// Inject keyframes once into document head (shared across all instances)
if (typeof document !== 'undefined' && !document.getElementById(FLASH_KEYFRAMES_ID)) {
  const style = document.createElement('style');
  style.id = FLASH_KEYFRAMES_ID;
  style.textContent = `
    @keyframes flashFade {
      from { background-color: rgba(13, 71, 161, 1); }
      to   { background-color: rgba(13, 71, 161, 0); }
    }
  `;
  document.head.appendChild(style);
}

function getFlashStyle(updatedAt) {
  if (!updatedAt) return undefined;
  const ageSec = (Date.now() - new Date(updatedAt).getTime()) / 1000;
  if (ageSec < 0 || ageSec > FLASH_DURATION_SEC) return undefined;
  return {
    animation: `flashFade ${FLASH_DURATION_SEC}s linear forwards`,
    animationDelay: `-${ageSec.toFixed(0)}s`,
  };
}

// ── Shared Intl date/time formatters (reused across renders) ──
const DATE_FMT = new Intl.DateTimeFormat('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const DATA_GRID_SX = {
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#1e1e1e', borderBottom: '2px solid #444',
    position: 'sticky', top: 0, zIndex: 2,
  },
  '& .MuiDataGrid-columnHeader': {
    userSelect: 'none', cursor: 'move', outline: 'none !important',
    '&:focus': { outline: 'none !important' },
    '&:focus-within': { outline: 'none !important' },
  },
  '& .MuiDataGrid-columnHeader--alignRight': { justifyContent: 'flex-end !important' },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    outline: 'none !important', '&:focus': { outline: 'none !important' },
  },
  '& .MuiDataGrid-cell': {
    color: '#ffffff !important', userSelect: 'none', padding: 0,
    borderRight: '1px solid rgba(255, 255, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
  '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
  '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
  '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0 },
  '& .MuiDataGrid-main': { padding: 0 },
  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
  '& .MuiSelect-icon': { color: '#ffffff !important' },
  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
  '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#ffffff !important', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
  },
  '& .MuiDataGrid-columnHeaderDraggableContainer': { cursor: 'move' },
};

// ══════════════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════════════

const THROTTLE_MS = 500;

const Odds = () => {
  const allBets = useStoreData('odds');
  const bets = useMemo(() => (allBets || []).filter(b => !b.pickem), [allBets]);
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useGridApiRef();

  // ── Logo lookup maps (resolved from reference tables, not per-record) ──
  const { bookLogos, teamMap } = useLogoMaps();

  // ── Available books: always derived from server-provided sportsbooks config (ingest=1) ──
  const availableBooks = useMemo(() => {
    return sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [sportsbooks]);

  // ── Filter state (persisted to localStorage) ──
  const [sport, setSport] = useState(() => localStorage.getItem('oddsSport') || 'NFL');
  const [market, setMarket] = useState(() => localStorage.getItem('oddsMarket') || 'Summary');
  const marketRef = useRef(market);
  marketRef.current = market;
  const [periodType, setPeriodType] = useState(() => localStorage.getItem('oddsPeriodType') || 'Full Game');

  // ── Throttled bets: leading+trailing — first update is immediate, subsequent buffered ──
  const [throttledBets, setThrottledBets] = useState(bets);
  const throttleRef = useRef(null);
  const latestBetsRef = useRef(bets);
  latestBetsRef.current = bets;

  useEffect(() => {
    // No active throttle → apply immediately (leading edge), start cooldown
    if (!throttleRef.current) {
      setThrottledBets(bets);
      throttleRef.current = setTimeout(() => {
        // Trailing edge: flush any updates that arrived during cooldown
        setThrottledBets(latestBetsRef.current);
        throttleRef.current = null;
      }, THROTTLE_MS);
    }
    // If throttle is active, latestBetsRef already captured the value —
    // it will be flushed when the timeout fires (trailing edge).
  }, [bets]);

  useEffect(() => {
    return () => { if (throttleRef.current) clearTimeout(throttleRef.current); };
  }, []);

  // (Flash-fade handled by pure CSS animation — no JS timer needed)

  // ── Selected books (persisted user intent — survives books dropping out of data) ──
  const [selectedBooks, setSelectedBooks] = useState(() => {
    const saved = localStorage.getItem(`oddsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`);
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { /* fall through */ }
    }
    return null; // null = "not initialized yet, default to all when data arrives"
  });

  // ── Odds history modal ──
  const { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory } = useOddsHistory();

  // Default to all books on first data load (only if user has no saved selection)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || availableBooks.length === 0) return;
    initializedRef.current = true;
    if (selectedBooks === null) {
      setSelectedBooks([...availableBooks]);
    }
  }, [availableBooks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedBooks !== null) {
      localStorage.setItem(`oddsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`, JSON.stringify(selectedBooks));
    }
  }, [selectedBooks, isMobile]);

  useEffect(() => { localStorage.setItem('oddsSport', sport); }, [sport]);
  useEffect(() => { localStorage.setItem('oddsMarket', market); }, [market]);
  useEffect(() => { localStorage.setItem('oddsPeriodType', periodType); }, [periodType]);

  // ── Volume toggle (persisted to localStorage) ──
  const [showVolume, setShowVolume] = useState(() => localStorage.getItem('oddsShowVolume') === 'true');
  const showVolumeRef = useRef(showVolume);
  showVolumeRef.current = showVolume;
  useEffect(() => { localStorage.setItem('oddsShowVolume', JSON.stringify(showVolume)); }, [showVolume]);

  // ── League options: from server-provided config (ingest=1 from DB) ──
  const leagueOptions = useMemo(() => {
    if (leaguesConfig.length > 0) {
      return leaguesConfig.map(l => l.name || l.id).filter(Boolean).sort();
    }
    // Fallback to data-derived leagues if config not loaded yet
    const fromData = [...new Set((throttledBets || []).map(b => b.league).filter(Boolean))];
    return fromData.length > 0 ? fromData.sort() : ['NBA'];
  }, [leaguesConfig, throttledBets]);

  // Reset sport if saved value no longer exists in available leagues.
  // Guard: only validate once the server-provided leagues config has loaded —
  // otherwise the ['NBA'] fallback fires on mount and clobbers the saved
  // sport (which cascades into market/periodType resets) before real data arrives.
  useEffect(() => {
    if (leaguesConfig.length === 0) return;
    if (leagueOptions.length > 0 && !leagueOptions.includes(sport)) {
      setSport(leagueOptions[0]);
    }
  }, [leaguesConfig, leagueOptions, sport]);

  // Resolve sport_id for the selected league (e.g. 'soccer', 'basketball')
  // Used for dynamic market mapping — soccer leagues all share the same mappings
  const currentSportId = useMemo(() => {
    const league = leaguesConfig.find(l => (l.name || l.id) === sport);
    return league?.sport_id || null;
  }, [leaguesConfig, sport]);

  // Market filter: sport-specific mapping with dynamic soccer support
  const getMarketFilter = useCallback((marketLabel) => {
    if (currentSportId === 'soccer') {
      const soccerMap = { Spread: 'asian_handicap', Total: 'total_goals', Moneyline: 'moneyline' };
      return soccerMap[marketLabel] || marketFilterMap[marketLabel];
    }
    return (marketFilterBySport[sport] && marketFilterBySport[sport][marketLabel]) || marketFilterMap[marketLabel];
  }, [currentSportId, sport]);

  const periodTypes = useMemo(() => {
    // Use currentSportId for sports where league names don't match config keys (soccer)
    let key = sport;
    if (currentSportId === 'soccer') key = '__soccer__';
    const all = periodTypesBySport[key] || periodTypesBySport[sport] || ['Full Game'];
    const restricted = noFullGameMarkets[key] || noFullGameMarkets[sport];
    if (restricted && restricted.has(market)) {
      return all.filter(p => p !== 'Full Game');
    }
    return all;
  }, [sport, market, currentSportId]);
  const marketOptions = useMemo(() => marketsBySport[sport] || defaultMarkets, [sport]);

  useEffect(() => {
    if (!periodTypes.includes(periodType)) setPeriodType(periodTypes[0] || 'Full Game');
  }, [periodTypes, periodType]);

  useEffect(() => {
    if (!marketOptions.includes(market)) setMarket(marketOptions[0] || 'Moneyline');
  }, [marketOptions, market]);

  const handleBooksFilterChange = useCallback((event) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      setSelectedBooks(prev => (prev || []).length === availableBooks.length ? [] : [...availableBooks]);
    } else {
      setSelectedBooks(value);
    }
  }, [availableBooks]);

  const renderBooksValue = useCallback((selected) => {
    if (!selected || !selected.length) return 'None';
    const visibleCount = selected.filter(b => availableBooks.includes(b)).length;
    if (visibleCount === availableBooks.length) return 'All';
    if (visibleCount > 1) return `Multiple (${visibleCount})`;
    return selected.filter(b => availableBooks.includes(b))[0] || 'None';
  }, [availableBooks]);

  // ══════════════════════════════════════════════════════════
  //  TABLE DATA — group throttled bets into rows
  // ══════════════════════════════════════════════════════════

  const rows = useMemo(() => {
    if (!Array.isArray(throttledBets)) return [];

    const filteredBets = throttledBets.filter(bet =>
      bet && bet.league === sport && bet.marketCategory === 'Game Line'
    );

    const eventGroups = {};
    filteredBets.forEach(bet => {
      const key = bet.fixture_id || bet.eventName;
      if (!eventGroups[key]) {
        eventGroups[key] = { event: bet.eventName, startTime: bet.eventStart, bets: [] };
      }
      eventGroups[key].bets.push(bet);
    });

    const result = [];
    const booksToRender = selectedBooks || [];

    const isSoccerML = currentSportId === 'soccer' && market === 'Moneyline';

    Object.entries(eventGroups).forEach(([eventKey, group]) => {
      const startDate = group.startTime ? new Date(group.startTime) : null;

      const row = {
        id: eventKey,
        eventName: group.event,
        isLive: group.bets.some(b => b.isLive),
        awayTeam: '', homeTeam: '',
        awayTeamLogo: '', homeTeamLogo: '',
        isThreeWay: isSoccerML,
        eventStart: startDate || new Date(),
        eventStartFormatted: {
          date: startDate ? DATE_FMT.format(startDate) : '',
          time: startDate ? TIME_FMT.format(startDate) : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { Away: '', Home: '', Draw: '', Over: '', Under: '', topRow: '', bottomRow: '' },
      };

      const isPlayerSport = sport === 'ATP' || sport === 'WTA' || sport === 'PGA';

      // Reference bets for team/player names
      const referenceBets = group.bets.filter(bet =>
        isPlayerSport ? bet.market === 'moneyline' : (bet.market === 'moneyline' || SPREAD_MARKETS.has(bet.market))
      );

      if (referenceBets.length > 0) {
        const reliableSources = ['DraftKings', 'FanDuel', 'Pinnacle'];
        const awayBet = referenceBets.find(b => b.side === 'Away' && reliableSources.includes(b.bookName))
                      || referenceBets.find(b => b.side === 'Away');
        let homeBet = referenceBets.find(b => b.side === 'Home' && reliableSources.includes(b.bookName))
                    || referenceBets.find(b => b.side === 'Home');

        if (isPlayerSport && awayBet && homeBet && awayBet.playerId === homeBet.playerId) {
          homeBet = referenceBets.find(b => b.side === 'Home' && b.playerId !== awayBet.playerId)
                  || referenceBets.find(b => b.playerId !== awayBet.playerId) || homeBet;
        }
        if (isPlayerSport) {
          row.awayTeam = awayBet ? (awayBet.playerName || awayBet.selection || 'Unknown Player') : 'Unknown Player';
          row.homeTeam = homeBet ? (homeBet.playerName || homeBet.selection || 'vs Unknown') : 'vs Unknown';
        } else {
          row.awayTeam = awayBet ? (teamMap[awayBet.awayTeamId]?.name || awayBet.selection || 'Unknown Team A') : 'Unknown Team A';
          row.homeTeam = homeBet ? (teamMap[homeBet.homeTeamId]?.name || homeBet.selection || 'Unknown Team B') : 'Unknown Team B';
          row.awayTeamLogo = awayBet ? (teamMap[awayBet.awayTeamId]?.logo || '') : '';
          row.homeTeamLogo = homeBet ? (teamMap[homeBet.homeTeamId]?.logo || '') : '';
          row.awayAbbr = awayBet ? (teamMap[awayBet.awayTeamId]?.abbreviation || '') : '';
          row.homeAbbr = homeBet ? (teamMap[homeBet.homeTeamId]?.abbreviation || '') : '';
        }
      } else {
        if (isPlayerSport) {
          const parts = group.event.includes(' vs ') ? group.event.split(' vs ') : group.event.split(' @ ');
          row.awayTeam = parts[0] ? parts[0].trim() : 'Unknown Player';
          row.homeTeam = parts[1] ? parts[1].trim() : 'vs Unknown';
        } else {
          const separator = group.event.includes(' vs ') ? ' vs ' : ' @ ';
          const [tA, tB] = group.event.split(separator);
          row.awayTeam = tA ? tA.trim().replace(/ -$/, '') : 'Unknown Team A';
          row.homeTeam = tB ? tB.trim().replace(/ -$/, '') : 'Unknown Team B';
          row.awayTeamLogo = teamMap[group.bets[0]?.awayTeamId]?.logo || '';
          row.homeTeamLogo = teamMap[group.bets[0]?.homeTeamId]?.logo || '';
          row.awayAbbr = teamMap[group.bets[0]?.awayTeamId]?.abbreviation || '';
          row.homeAbbr = teamMap[group.bets[0]?.homeTeamId]?.abbreviation || '';
        }
      }

      // ── SUMMARY VIEW ──
      if (market === 'Summary') {
        const spreadBets = group.bets.filter(b => SPREAD_MARKETS.has(b.market) && b.periodType === periodType);
        const totalBets = group.bets.filter(b => TOTAL_MARKETS.has(b.market) && b.periodType === periodType);

        const spreadOutcomes = { Away: {}, Home: {} };
        const spreadDetails  = { Away: {}, Home: {} };
        const spreadUpdated  = { Away: {}, Home: {} };

        spreadBets.forEach(bet => {
          const s = bet.side;
          spreadOutcomes[s][bet.bookName] = bet.price;
          spreadDetails[s][`${bet.bookName}Points`] = bet.points;
          spreadDetails[s][`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
          spreadDetails[s][`${bet.bookName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          spreadUpdated[s][bet.bookName] = bet.updatedAt;
          spreadDetails[s][`${bet.bookName}Limits`] = bet.limits;
          spreadDetails[s][`${bet.bookName}Meta`] = {
            fixture_id: bet.fixture_id,
            market_id: bet.market_id,
            selection: bet.selection,
            selection_line: bet.selection_line,
            player_id: bet.playerId || '',
            points: bet.points,
            playerName: bet.playerName || `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`,
            marketDisplay: bet.marketName || 'Spread',
            league: sport,
            awayTeam: row.awayAbbr || row.awayTeam,
            homeTeam: row.homeAbbr || row.homeTeam,
            awayTeamLogo: row.awayTeamLogo,
            homeTeamLogo: row.homeTeamLogo,
            eventStart: row.eventStart,
            bookLogo: bookLogos[bet.bookName] || '',
          };
        });

        let favoriteId = 'Away';
        for (const book of booksToRender) {
          const awayPts = spreadDetails.Away[`${book}Points`];
          const homePts = spreadDetails.Home[`${book}Points`];
          if (awayPts !== undefined && homePts !== undefined) {
            favoriteId = awayPts < 0 ? 'Away' : 'Home';
            break;
          }
        }

        const totalOutcomes = { Over: {} };
        const totalDetails  = { Over: {} };
        const totalUpdated  = { Over: {} };

        totalBets.forEach(bet => {
          if (bet.side === 'Over') {
            totalOutcomes.Over[bet.bookName] = bet.price;
            totalDetails.Over[`${bet.bookName}Points`] = bet.points;
            totalDetails.Over[`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
            totalDetails.Over[`${bet.bookName}LineDisplay`] = `o${bet.points || ''}`;
            totalUpdated.Over[bet.bookName] = bet.updatedAt;
            totalDetails.Over[`${bet.bookName}Limits`] = bet.limits;
            totalDetails.Over[`${bet.bookName}Meta`] = {
              fixture_id: bet.fixture_id,
              market_id: bet.market_id,
              selection: bet.selection,
              selection_line: bet.selection_line,
              player_id: bet.playerId || '',
              points: bet.points,
              playerName: bet.playerName || `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`,
              marketDisplay: bet.marketName || 'Total',
              league: sport,
              awayTeam: row.awayAbbr || row.awayTeam,
              homeTeam: row.homeAbbr || row.homeTeam,
              awayTeamLogo: row.awayTeamLogo,
              homeTeamLogo: row.homeTeamLogo,
              eventStart: row.eventStart,
              bookLogo: bookLogos[bet.bookName] || '',
            };
          }
        });

        const bestOdds = {};
        const favoriteSpreadEntries = [];
        booksToRender.forEach(book => {
          const price = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
          const points = spreadDetails[favoriteId][`${book}Points`];
          if (price !== null) {
            favoriteSpreadEntries.push({
              book, price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: spreadDetails[favoriteId][`${book}LineDisplay`],
              logo: spreadDetails[favoriteId][`${book}Logo`],
            });
          }
        });

        const isSpreadTopRow = favoriteId === 'Away';
        const spreadRowKey = isSpreadTopRow ? 'topRow' : 'bottomRow';
        const totalRowKey = isSpreadTopRow ? 'bottomRow' : 'topRow';

        if (favoriteSpreadEntries.length > 0) {
          favoriteSpreadEntries.sort((a, b) => b.price - a.price);
          const best = favoriteSpreadEntries[0];
          bestOdds[spreadRowKey] = {
            price: best.price, lineDisplay: best.lineDisplay, logo: best.logo,
            display: `${best.lineDisplay} ${best.price > 0 ? '+' : ''}${best.price}`, type: 'spread',
            meta: spreadDetails[favoriteId][`${best.book}Meta`] || null,
          };
          row.bestOddsBookmakers[spreadRowKey] = best.book;
        } else {
          bestOdds[spreadRowKey] = { display: '', logo: '', type: 'spread' };
        }

        const overEntries = [];
        booksToRender.forEach(book => {
          const price = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
          const points = totalDetails.Over[`${book}Points`];
          if (price !== null) {
            overEntries.push({
              book, price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: totalDetails.Over[`${book}LineDisplay`],
              logo: totalDetails.Over[`${book}Logo`],
            });
          }
        });

        if (overEntries.length > 0) {
          overEntries.sort((a, b) => a.points !== b.points ? a.points - b.points : b.price - a.price);
          const best = overEntries[0];
          bestOdds[totalRowKey] = {
            price: best.price, lineDisplay: best.lineDisplay, logo: best.logo,
            display: `${best.lineDisplay} ${best.price > 0 ? '+' : ''}${best.price}`, type: 'total',
            meta: totalDetails.Over[`${best.book}Meta`] || null,
          };
          row.bestOddsBookmakers[totalRowKey] = best.book;
        } else {
          bestOdds[totalRowKey] = { display: '', logo: '', type: 'total' };
        }

        row.summaryMetadata = { favoriteId, isSpreadTopRow };

        booksToRender.forEach(book => {
          const odds = { topRow: '', bottomRow: '', topRowUpdatedDate: null, bottomRowUpdatedDate: null, topRowMeta: null, bottomRowMeta: null };

          if (isSpreadTopRow) {
            const sp = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (sp !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${sp > 0 ? '+' : ''}${sp}`;
              odds.topRowUpdatedDate = spreadUpdated[favoriteId][book];
              odds.topRowMeta = spreadDetails[favoriteId][`${book}Meta`] || null;
              odds.topRowLimits = spreadDetails[favoriteId][`${book}Limits`] || null;
            }
            const op = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
            if (op !== null) {
              const line = totalDetails.Over[`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${op > 0 ? '+' : ''}${op}`;
              odds.bottomRowUpdatedDate = totalUpdated.Over[book];
              odds.bottomRowMeta = totalDetails.Over[`${book}Meta`] || null;
              odds.bottomRowLimits = totalDetails.Over[`${book}Limits`] || null;
            }
          } else {
            const op = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
            if (op !== null) {
              const line = totalDetails.Over[`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${op > 0 ? '+' : ''}${op}`;
              odds.topRowUpdatedDate = totalUpdated.Over[book];
              odds.topRowMeta = totalDetails.Over[`${book}Meta`] || null;
              odds.topRowLimits = totalDetails.Over[`${book}Limits`] || null;
            }
            const sp = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (sp !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${sp > 0 ? '+' : ''}${sp}`;
              odds.bottomRowUpdatedDate = spreadUpdated[favoriteId][book];
              odds.bottomRowMeta = spreadDetails[favoriteId][`${book}Meta`] || null;
              odds.bottomRowLimits = spreadDetails[favoriteId][`${book}Limits`] || null;
            }
          }

          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        row.bestOdds = bestOdds;

      } else {
        // ── SINGLE MARKET VIEW ──
        const targetMarket = getMarketFilter(market);
        const oddsBets = group.bets.filter(b => b.market === targetMarket && b.periodType === periodType);

        const isMLOrSpread = market === 'Moneyline' || market === 'Spread';
        const outcomeKeys = isSoccerML ? ['Away', 'Draw', 'Home'] : (isMLOrSpread ? ['Away', 'Home'] : ['Over', 'Under']);
        row.outcomeKeys = outcomeKeys;

        const outcomes = {};
        const outcomeDetails = {};
        const outcomeUpdatedDates = {};
        outcomeKeys.forEach(k => { outcomes[k] = {}; outcomeDetails[k] = {}; outcomeUpdatedDates[k] = {}; });

        oddsBets.forEach(bet => {
          const outcome = bet.side;
          if (!outcomes[outcome]) return;

          outcomes[outcome][bet.bookName] = bet.price;
          outcomeDetails[outcome][`${bet.bookName}Points`] = bet.points;
          outcomeDetails[outcome][`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
          outcomeDetails[outcome][`${bet.bookName}OutcomeName`] = outcome;
          outcomeUpdatedDates[outcome][bet.bookName] = bet.updatedAt;
          outcomeDetails[outcome][`${bet.bookName}Limits`] = bet.limits;
          outcomeDetails[outcome][`${bet.bookName}Meta`] = {
            fixture_id: bet.fixture_id,
            market_id: bet.market_id,
            selection: bet.selection,
            selection_line: bet.selection_line,
            player_id: bet.playerId || '',
            points: bet.points,
            playerName: bet.playerName || (market === 'Moneyline' ? (bet.selection || '') : `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`),
            marketDisplay: bet.marketName || market || '',
            league: sport,
            awayTeam: row.awayAbbr || row.awayTeam,
            homeTeam: row.homeAbbr || row.homeTeam,
            awayTeamLogo: row.awayTeamLogo,
            homeTeamLogo: row.homeTeamLogo,
            eventStart: row.eventStart,
            bookLogo: bookLogos[bet.bookName] || '',
          };

          if (market === 'Spread') {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          } else if (market === 'Total') {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = `${outcome === 'Over' ? 'o' : 'u'}${bet.points || ''}`;
          } else {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = '';
          }
        });

        const bestOdds = {};
        outcomeKeys.forEach(outcome => {
          const entries = [];
          booksToRender.forEach(book => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            const points = outcomeDetails[outcome][`${book}Points`];
            if (price !== null) {
              entries.push({
                book, price,
                points: points !== undefined ? parseFloat(points) : null,
                logo: outcomeDetails[outcome][`${book}Logo`],
                outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
                lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
              });
            }
          });

          let bestEntry = null;
          if (entries.length > 0) {
            if (market === 'Total') {
              entries.sort((a, b) => {
                if (a.points !== b.points) return outcome === 'Over' ? a.points - b.points : b.points - a.points;
                return b.price - a.price;
              });
            } else {
              entries.sort((a, b) => b.price - a.price);
            }
            bestEntry = entries[0];
          }

          if (bestEntry) {
            bestOdds[outcome] = {
              price: bestEntry.price, points: bestEntry.points, logo: bestEntry.logo,
              outcomeName: bestEntry.outcomeName, lineDisplay: bestEntry.lineDisplay,
              meta: outcomeDetails[outcome][`${bestEntry.book}Meta`] || null,
            };
            row.bestOddsBookmakers[outcome] = bestEntry.book;
          } else {
            bestOdds[outcome] = { price: null, points: null, logo: '', outcomeName: outcome, lineDisplay: '' };
            row.bestOddsBookmakers[outcome] = '';
          }
        });

        booksToRender.forEach(book => {
          const odds = { updatedDates: {}, meta: {}, limits: {} };
          outcomeKeys.forEach(outcome => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            if (price !== null) {
              const fmt = price > 0 ? `+${price}` : `${price}`;
              const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
              odds[outcome] = line ? `${line} ${fmt}` : fmt;
              odds.updatedDates[outcome] = outcomeUpdatedDates[outcome][book];
              odds.meta[outcome] = outcomeDetails[outcome][`${book}Meta`] || null;
              odds.limits[outcome] = outcomeDetails[outcome][`${book}Limits`] || null;
            } else {
              odds[outcome] = '';
              odds.updatedDates[outcome] = null;
              odds.meta[outcome] = null;
              odds.limits[outcome] = null;
            }
          });
          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        outcomeKeys.forEach(outcome => {
          const { price, lineDisplay } = bestOdds[outcome] || {};
          if (price !== null && price !== undefined) {
            const fmt = price > 0 ? `+${price}` : `${price}`;
            bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${fmt}` : fmt;
          } else {
            bestOdds[outcome].display = '';
          }
        });

        row.bestOdds = bestOdds;
      }

      result.push(row);
    });

    result.sort((a, b) => a.eventStart - b.eventStart);
    return result;
  }, [throttledBets, sport, market, periodType, selectedBooks, bookLogos, teamMap, getMarketFilter, currentSportId]);

  // ══════════════════════════════════════════════════════════
  //  COLUMN ORDER PERSISTENCE — keyed by sport + device only
  // ══════════════════════════════════════════════════════════

  const getSavedColumnOrder = useCallback(() => {
    try {
      const saved = localStorage.getItem('oddsColumnOrder');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return null;
  }, []);

  const saveColumnOrder = useCallback((order) => {
    try {
      localStorage.setItem('oddsColumnOrder', JSON.stringify(order));
    } catch (e) { /* ignore */ }
  }, []);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS — does NOT depend on `market` (renderCell reads it via ref)
  // ══════════════════════════════════════════════════════════

  const columns = useMemo(() => {
    const isPlayerSportCol = sport === 'ATP' || sport === 'WTA' || sport === 'PGA';
    const baseColumns = [
      {
        field: 'eventName', headerName: '',
        minWidth: isMobile ? (isPlayerSportCol ? 90 : 35) : 230, width: isMobile ? (isPlayerSportCol ? 90 : 35) : undefined,
        flex: 1, sortable: false, disableReorder: true,
        renderCell: (params) => {
          const lastName = (name) => (name || '').split(' ').pop();
          return (
          <Box sx={SX_COL_OUTER}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
              {params.row.awayTeamLogo && (
                <img src={params.row.awayTeamLogo} alt={`${params.row.awayTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }} />
              )}
              {(!isMobile || isPlayerSportCol) && <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{isMobile ? lastName(params.row.awayTeam) : params.row.awayTeam}</Typography>}
            </Box>
            {params.row.isThreeWay && (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
                {!isMobile && <Typography sx={{ fontSize: '14px', color: '#888' }}>Draw</Typography>}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
              {params.row.homeTeamLogo && (
                <img src={params.row.homeTeamLogo} alt={`${params.row.homeTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }} />
              )}
              {(!isMobile || isPlayerSportCol) && <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{isMobile ? lastName(params.row.homeTeam) : params.row.homeTeam}</Typography>}
            </Box>
          </Box>
          );
        },
      },
      {
        field: 'eventStartFormatted', headerName: '',
        minWidth: isMobile ? 70 : 85, maxWidth: isMobile ? 70 : 85,
        sortable: false, disableReorder: true,
        renderCell: (params) => (
          <Box sx={SX_COL_OUTER}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            {params.row.isThreeWay && <Box sx={{ height: '35px' }} />}
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              {params.row.isLive ? (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  px: '6px', py: '1px', borderRadius: '4px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                }}>
                  <Box sx={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }} />
                  <Typography sx={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 600, color: '#ef4444', letterSpacing: '0.5px' }}>
                    LIVE
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds', headerName: 'Best Odds', headerAlign: 'right',
        minWidth: isMobile ? 110 : 155, flex: 1, sortable: false, disableReorder: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          const m = marketRef.current;
          if (m === 'Summary') {
            return (
              <Box sx={SX_COL_OUTER}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                  cursor: value.topRow?.display ? 'pointer' : 'default',
                  '&:hover': value.topRow?.display ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} onClick={() => value.topRow?.meta && openOddsHistory(value.topRow.meta, params.row.bestOddsBookmakers?.topRow)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                    {value.topRow?.display || ''}
                  </Typography>
                  {value.topRow?.logo && <Tooltip title={params.row.bestOddsBookmakers?.topRow || ''} placement="top" arrow><img src={value.topRow.logo} alt="Best" style={STYLE_BEST_IMG} /></Tooltip>}
                </Box>
                <Box sx={{
                  display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                  cursor: value.bottomRow?.display ? 'pointer' : 'default',
                  '&:hover': value.bottomRow?.display ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} onClick={() => value.bottomRow?.meta && openOddsHistory(value.bottomRow.meta, params.row.bestOddsBookmakers?.bottomRow)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                    {value.bottomRow?.display || ''}
                  </Typography>
                  {value.bottomRow?.logo && <Tooltip title={params.row.bestOddsBookmakers?.bottomRow || ''} placement="top" arrow><img src={value.bottomRow.logo} alt="Best" style={STYLE_BEST_IMG} /></Tooltip>}
                </Box>
              </Box>
            );
          }
          const outcomeKeys = params.row.outcomeKeys || (m === 'Moneyline' || m === 'Spread' ? ['Away', 'Home'] : ['Over', 'Under']);
          return (
            <Box sx={SX_COL_OUTER}>
              {outcomeKeys.map(outcome => {
                const { display, logo, outcomeName, meta } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{
                    display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }} onClick={() => meta && openOddsHistory(meta, params.row.bestOddsBookmakers?.[outcome])}>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                      {display}
                    </Typography>
                    {logo && <Tooltip title={params.row.bestOddsBookmakers?.[outcome] || ''} placement="top" arrow><img src={logo} alt={`${outcomeName} Logo`} style={STYLE_BEST_IMG} /></Tooltip>}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    const visibleBookmakers = (selectedBooks || []).filter(b => availableBooks.includes(b));
    const bookmakerColumns = {};

    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book, headerName: '', headerAlign: 'right',
        minWidth: isMobile ? 90 : 115, flex: 1, sortable: false, disableReorder: false,
        renderHeader: () => {
          const hasLink = !!bookmakerLinks[book];
          return (
            <Box
              sx={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                width: '100%', height: '100%', paddingRight: isMobile ? '4px' : '12px',
                cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none',
              }}
              onClick={(e) => {
                if (hasLink && e.detail > 0) window.open(bookmakerLinks[book], '_blank', 'noopener,noreferrer');
              }}
            >
              {bookLogos[book] ? (
                <Tooltip title={book} placement="bottom" arrow>
                  <img src={bookLogos[book]} alt={book}
                    style={{ maxWidth: isMobile ? '40px' : '60px', maxHeight: isMobile ? '28px' : '36px',
                      width: 'auto', height: 'auto', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '10px' : '12px', color: '#ffffff', textAlign: 'right' }}>{book}</Typography>
              )}
            </Box>
          );
        },
        renderCell: (params) => {
          const bookOdds = params.row.bookmakerOdds && params.row.bookmakerOdds[params.field];
          const bestBookmaker = params.row.bestOddsBookmakers;
          const m = marketRef.current;

          if (m === 'Summary') {
            if (!bookOdds) return <Box sx={SX_EMPTY_CELL}><Typography sx={{ fontSize: '14px' }} /></Box>;
            const topFlash = getFlashStyle(bookOdds.topRowUpdatedDate);
            const botFlash = getFlashStyle(bookOdds.bottomRowUpdatedDate);
            return (
              <Box sx={SX_COL_OUTER}>
                <Box sx={{
                  height: '35px', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%',
                  cursor: bookOdds.topRow ? 'pointer' : 'default',
                  border: params.field === bestBookmaker.topRow ? '2px solid #00cc00' : 'none',
                  '&:hover': bookOdds.topRow ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} style={topFlash} onClick={() => bookOdds.topRowMeta && openOddsHistory(bookOdds.topRowMeta, params.field)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1.2 }}>
                    {bookOdds.topRow || ''}
                  </Typography>
                  {showVolumeRef.current && (() => { const v = formatLimitMax(bookOdds.topRowLimits); return v ? <Typography sx={{ color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1 }}>{v}</Typography> : null; })()}
                </Box>
                <Box sx={{
                  height: '35px', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%',
                  cursor: bookOdds.bottomRow ? 'pointer' : 'default',
                  border: params.field === bestBookmaker.bottomRow ? '2px solid #00cc00' : 'none',
                  '&:hover': bookOdds.bottomRow ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} style={botFlash} onClick={() => bookOdds.bottomRowMeta && openOddsHistory(bookOdds.bottomRowMeta, params.field)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1.2 }}>
                    {bookOdds.bottomRow || ''}
                  </Typography>
                  {showVolumeRef.current && (() => { const v = formatLimitMax(bookOdds.bottomRowLimits); return v ? <Typography sx={{ color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1 }}>{v}</Typography> : null; })()}
                </Box>
              </Box>
            );
          }

          if (!bookOdds) return <Box sx={SX_EMPTY_CELL}><Typography sx={{ fontSize: '14px' }} /></Box>;
          const outcomeKeys = params.row.outcomeKeys || (m === 'Moneyline' || m === 'Spread' ? ['Away', 'Home'] : ['Over', 'Under']);
          return (
            <Box sx={SX_COL_OUTER}>
              {outcomeKeys.map(outcome => {
                const flash = getFlashStyle(bookOdds.updatedDates?.[outcome]);
                const meta = bookOdds.meta?.[outcome];
                return (
                  <Box key={outcome} sx={{
                    height: '35px', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%',
                    cursor: bookOdds[outcome] ? 'pointer' : 'default',
                    border: params.field === bestBookmaker[outcome] ? '2px solid #00cc00' : 'none',
                    '&:hover': bookOdds[outcome] ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                  }} style={flash} onClick={() => meta && openOddsHistory(meta, params.field)}>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1.2 }}>
                      {bookOdds[outcome] || ''}
                    </Typography>
                    {showVolumeRef.current && (() => { const v = formatLimitMax(bookOdds.limits?.[outcome]); return v ? <Typography sx={{ color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: isMobile ? '4px' : '12px', lineHeight: 1 }}>{v}</Typography> : null; })()}
                  </Box>
                );
              })}
            </Box>
          );
        },
      };
    });

    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    if (savedOrder && savedOrder.bookmakers) {
      savedOrder.bookmakers.forEach(book => { if (bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]); });
      visibleBookmakers.forEach(book => { if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]); });
    } else {
      visibleBookmakers.forEach(book => finalColumns.push(bookmakerColumns[book]));
    }
    return finalColumns;
  }, [sport, isMobile, selectedBooks, bookLogos, getSavedColumnOrder, showVolume]);

  const handleColumnOrderChange = useCallback(() => {
    if (!apiRef.current) return;
    try {
      const orderedFields = apiRef.current.getAllColumns().map(c => c.field);
      const bookmakers = orderedFields.filter(f => !FIXED_COLUMNS.includes(f));
      saveColumnOrder({ fixed: FIXED_COLUMNS, bookmakers });
    } catch (e) { /* ignore */ }
  }, [apiRef, saveColumnOrder]);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: 'background.default' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px' }}>
            <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2} alignItems={isMobile ? "stretch" : "center"}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="sport-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                <Select labelId="sport-filter-label" label="League" notched value={sport} onChange={(e) => setSport(e.target.value)}>
                  {leagueOptions.map(league => (
                    <MenuItem key={league} value={league}>{league}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="market-filter-label" shrink sx={{ color: '#ffffff' }}>Market</InputLabel>
                <Select labelId="market-filter-label" label="Market" notched value={market} onChange={(e) => setMarket(e.target.value)}>
                  {marketOptions.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="period-type-filter-label" shrink sx={{ color: '#ffffff' }}>Period Type</InputLabel>
                <Select labelId="period-type-filter-label" label="Period Type" notched value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
                  {periodTypes.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 140 }}>
                <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
                <Select multiple labelId="books-filter-label" label="Books" notched
                  value={selectedBooks || []} onChange={handleBooksFilterChange} renderValue={renderBooksValue}>
                  <MenuItem value="all">
                    <Checkbox checked={(selectedBooks || []).length === availableBooks.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {availableBooks.map((book) => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={(selectedBooks || []).includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox checked={showVolume} onChange={() => setShowVolume(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, p: '4px' }} />}
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Vol</Typography>}
                sx={{ ml: 0, mr: 0 }}
              />
            </Stack>
          </Box>
          <Box className="table-section" sx={{ height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 88px)', overflowY: 'auto' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
              {rows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">No odds data available for the selected filters.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef} rows={rows} columns={columns}
                    disableSelectionOnClick disableRowSelectionOnClick
                    disableColumnSort disableColumnMenu disableColumnSelector disableColumnFilter
                    hideFooter getRowHeight={(params) => params.model?.isThreeWay ? 105 : 70}
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={DATA_GRID_SX}
                    rowBuffer={5} rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>

      <OddsHistoryModal
        open={historyOpen}
        onClose={closeOddsHistory}
        data={historyData}
        loading={historyLoading}
        meta={historyMeta}
      />
    </ThemeProvider>
  );
};

export default Odds;